/** Score dimensions used by the candidate scoring UI (SubScoreBars/Grid).
 * Keys are stable — keep in sync with the scoring service once Phase 5
 * brings live scoring in. Icons and labels for display live in
 * @vidario/ui/scores; this module stays UI-free so it can ship to any
 * runtime. */
export const SCORE_KEYS = ["skills", "experience", "industry", "location"] as const;
export type ScoreKey = (typeof SCORE_KEYS)[number];

export type SubScores = Partial<Record<ScoreKey, number>>;

/** Bucket thresholds used for color coding. Also exposed from the
 * prototype's score ramp. */
export const SCORE_BUCKETS = {
  excellent: 85,
  good: 70,
  fair: 50,
} as const;

/** Pick which bucket a 0–100 score falls into. */
export function bucketFor(n: number): "excellent" | "good" | "fair" | "poor" {
  if (n >= SCORE_BUCKETS.excellent) return "excellent";
  if (n >= SCORE_BUCKETS.good) return "good";
  if (n >= SCORE_BUCKETS.fair) return "fair";
  return "poor";
}

/** Default dimension weights used when aggregating sub-scores into an
 * overall score. Sum to 1. Kept wide-open for future tuning. */
export const DEFAULT_SCORE_WEIGHTS: Record<ScoreKey, number> = {
  skills: 0.4,
  experience: 0.3,
  industry: 0.2,
  location: 0.1,
};

/** Weighted average of sub-scores. Missing keys count as 0. */
export function weightedScore(
  sub: SubScores,
  weights: Record<ScoreKey, number> = DEFAULT_SCORE_WEIGHTS,
): number {
  let total = 0;
  for (const k of SCORE_KEYS) {
    total += (sub[k] ?? 0) * weights[k];
  }
  return Math.round(total);
}
